import { PUBLIC_CHAIN_ID, PUBLIC_TOKEN_ADDRESS, PUBLIC_TOKEN_ADDRESS_DC } from '$env/static/public';

import { writable, type Writable } from 'svelte/store';

import { ArtAssetType } from '@do/models';
import { useReadContract, useWaitTransaction, useWriteContract } from '@do/svelte-web3-manager';

import type { HashString } from '@shared/types';

import { defaultData } from '../config';
import erc20ABIweb from '../config/erc20ABI.json';
import TokenABI from '../config/TokenABI.json';
import { type ApprovalResponse, type PurchaseModel, PurchaseState } from '../types';

const createModel = (modelData: PurchaseModel) => {
  const { set, subscribe, update }: Writable<PurchaseModel> = writable(modelData);
  let writeContract: ReturnType<typeof useWriteContract>;
  let readContract: ReturnType<typeof useReadContract>;
  let waitForTransaction: ReturnType<typeof useWaitTransaction>;

  const checkVariables = () => {
    if (!PUBLIC_TOKEN_ADDRESS) {
      console.error('Public token address not found');
      return;
    }
    if (!PUBLIC_CHAIN_ID) {
      console.error('Chain ID not found');
      return;
    }
  };

  return {
    subscribe,
    set,
    update,
    init() {
      checkVariables();
      writeContract = useWriteContract();
      readContract = useReadContract();
      waitForTransaction = useWaitTransaction();
    },
    async buy(approvalResponse: ApprovalResponse, artAssetType: ArtAssetType) {
      const address = (
        artAssetType === ArtAssetType.DigitalOriginal ? PUBLIC_TOKEN_ADDRESS : PUBLIC_TOKEN_ADDRESS_DC
      ) as HashString;

      const hash = await writeContract({
        address,
        abi: TokenABI,
        functionName: 'buy',
        args: [
          {
            tokenId: BigInt(approvalResponse.tokenId),
            tokenURI: approvalResponse.tokenURI,
            price: BigInt(approvalResponse.price),
            fee: BigInt(approvalResponse.fee),
            participants: approvalResponse.participants,
            shares: approvalResponse.shares,
            signature: approvalResponse.signature,
            deadline: approvalResponse.deadline,
          },
        ],
      });
      const response = await waitForTransaction({ hash });

      if (response.status !== 'success') throw { shortMessage: 'An error occurred' };
    },
    async approveERC20Transfer(
      contractAddress: HashString,
      amount: bigint,
      walletAddress: HashString,
      artAssetType: ArtAssetType,
    ) {
      const balance = (await readContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'balanceOf',
        args: [walletAddress],
      })) as bigint;

      // if the real balance is less than the amount, throw an error
      if (balance < amount)
        throw {
          shortMessage: 'Insufficient funds to complete the transaction. Please top up your USDC balance.',
        };

      const tokenAddress =
        artAssetType === ArtAssetType.DigitalOriginal ? PUBLIC_TOKEN_ADDRESS : PUBLIC_TOKEN_ADDRESS_DC;

      const allowance = (await readContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'allowance',
        args: [walletAddress, tokenAddress],
      })) as bigint;

      if (allowance >= amount) {
        console.info('Already approved');
        set({ state: PurchaseState.Pending }); // as we skip approve step, we set this state here
        return;
      }

      const hash = await writeContract({
        address: contractAddress,
        abi: erc20ABIweb,
        functionName: 'approve',
        args: [tokenAddress, amount],
      });

      const resApprove = await waitForTransaction({ hash });

      if (resApprove.status !== 'success') throw { shortMessage: 'Error during transaction approve' };

      set({ state: PurchaseState.Pending });
    },
    async getOwner(tokenId: number, artAssetType: ArtAssetType) {
      const address = artAssetType === ArtAssetType.DigitalOriginal ? PUBLIC_TOKEN_ADDRESS : PUBLIC_TOKEN_ADDRESS_DC;

      return await readContract({
        address: address as HashString,
        abi: TokenABI,
        functionName: 'ownerOf',
        args: [tokenId],
      })
        .then((res) => res as string)
        .catch(() => null);
    },
  };
};

export const purchaseModel = createModel(defaultData);
